<template>
  <LineChart
    :chart-options="computedOptions"
    :chart-data="chartData"
    :chart-id="chartId"
    :dataset-id-key="datasetIdKey"
    :plugins="chartPlugins"
    :css-classes="cssClasses"
    :styles="myStyles"
    @chart:render="onChartRender"
  />
</template>

<script>
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  Filler,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  Filler,
  zoomPlugin,
);

export default {
  name: "EnhancedLineChart",
  components: {
    LineChart: Line,
  },
  props: {
    chartData: {
      type: Object,
      default: () => ({ labels: [], datasets: [] }),
    },
    chartId: {
      type: String,
      default: "enhanced-line-chart",
    },
    chartOptions: {
      type: Object,
      default: () => ({}),
    },
    datasetIdKey: {
      type: String,
      default: "label",
    },
    cssClasses: {
      default: "",
      type: String,
    },
    styles: {
      type: Object,
      default: () => ({}),
    },
    plugins: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["point-click", "chart-hover"],
  computed: {
    myStyles() {
      return {
        height: "500px",
        position: "relative",
        ...this.styles,
      };
    },

    chartPlugins() {
      return [
        ...this.plugins,
        zoomPlugin,
        {
          id: "performance-zones",
          beforeDraw: (chart) => {
            this.drawPerformanceZones(chart);
          },
        },
        {
          id: "trend-lines",
          afterDatasetsDraw: (chart) => {
            this.drawTrendLines(chart);
          },
        },
      ];
    },

    computedOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                size: 12,
                family: "Inter, sans-serif",
              },
              generateLabels: (chart) => {
                return this.generateCustomLabels(chart);
              },
            },
          },
          tooltip: {
            enabled: true,
            mode: "index",
            intersect: false,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "rgba(255, 255, 255, 0.1)",
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: true,
            callbacks: {
              title: (context) => {
                return `Date: ${context[0].label}`;
              },
              label: (context) => {
                const unit = this.getDatasetUnit(context.dataset.label);
                return `${context.dataset.label}: ${context.parsed.y}${unit}`;
              },
              afterBody: (context) => {
                return this.getTooltipInsights(context);
              },
            },
          },
          zoom: {
            limits: {
              x: { min: "original", max: "original" },
              y: { min: "original", max: "original" },
            },
            pan: {
              enabled: true,
              mode: "x",
              threshold: 10,
            },
            zoom: {
              wheel: {
                enabled: true,
                speed: 0.1,
              },
              pinch: {
                enabled: true,
              },
              mode: "x",
            },
          },
        },
        scales: {
          y: {
            beginAtZero: false,
            grid: {
              color: "rgba(0,0,0,0.1)",
              drawBorder: false,
            },
            title: {
              display: true,
              text: this.getYAxisLabel(),
              font: {
                size: 14,
                weight: "600",
              },
            },
            ticks: {
              callback: (value) => {
                return value + this.getCurrentUnit();
              },
              font: {
                size: 11,
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            title: {
              display: true,
              text: "Date",
              font: {
                size: 14,
                weight: "600",
              },
            },
            ticks: {
              font: {
                size: 11,
              },
              maxRotation: 45,
              minRotation: 0,
            },
          },
        },
        interaction: {
          mode: "nearest",
          axis: "x",
          intersect: false,
        },
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const element = elements[0];
            this.$emit("point-click", element.index, element.datasetIndex);
          }
        },
        onHover: (event, elements) => {
          this.$emit("chart-hover", event, elements);
        },
        animation: {
          duration: 1000,
          easing: "easeInOutQuart",
        },
        elements: {
          line: {
            tension: 0.4,
            borderWidth: 2,
          },
          point: {
            radius: 4,
            hoverRadius: 6,
            borderWidth: 2,
            backgroundColor: "white",
          },
        },
        ...this.chartOptions,
      };
    },
  },

  methods: {
    onChartRender() {
      // Chart has been rendered
      this.$emit("chart:render");
    },

    drawPerformanceZones(chart) {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;

      // Define performance zones based on chart type
      const zones = this.getPerformanceZones();

      if (!zones || zones.length === 0) return;

      zones.forEach((zone) => {
        const yMin = chart.scales.y.getPixelForValue(zone.yMin);
        const yMax = chart.scales.y.getPixelForValue(zone.yMax);

        ctx.save();
        ctx.fillStyle = zone.backgroundColor;
        ctx.fillRect(
          chartArea.left,
          Math.min(yMin, yMax),
          chartArea.right - chartArea.left,
          Math.abs(yMax - yMin),
        );
        ctx.restore();
      });
    },

    drawTrendLines(chart) {
      const ctx = chart.ctx;

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        if (dataset.showTrend === false) return;

        const data = dataset.data.filter(
          (value) => value !== null && value !== undefined,
        );
        if (data.length < 2) return;

        const trendData = this.calculateTrendLine(data);
        if (!trendData || trendData.length < 2) return;

        const meta = chart.getDatasetMeta(datasetIndex);
        const points = meta.data;

        if (points.length < 2) return;

        ctx.save();
        ctx.strokeStyle = dataset.borderColor || "#999";
        ctx.setLineDash([5, 5]);
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.6;

        ctx.beginPath();
        ctx.moveTo(points[0].x, chart.scales.y.getPixelForValue(trendData[0]));
        ctx.lineTo(
          points[points.length - 1].x,
          chart.scales.y.getPixelForValue(trendData[trendData.length - 1]),
        );
        ctx.stroke();

        ctx.restore();
      });
    },

    calculateTrendLine(data) {
      if (data.length < 2) return null;

      const n = data.length;
      const sumX = data.reduce((sum, _, index) => sum + index, 0);
      const sumY = data.reduce((sum, value) => sum + value, 0);
      const sumXY = data.reduce((sum, value, index) => sum + index * value, 0);
      const sumXX = data.reduce((sum, _, index) => sum + index * index, 0);

      const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;

      return data.map((_, index) => slope * index + intercept);
    },

    getPerformanceZones() {
      // This would be customized based on the metric type
      // For now, returning null to disable zones
      return null;
    },

    generateCustomLabels(chart) {
      const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
      const labels = original.call(this, chart);

      // Customize labels to show additional information
      return labels.map((label) => {
        const dataset = chart.data.datasets[label.datasetIndex];

        // Add trend indicator to label
        if (dataset.data && dataset.data.length > 1) {
          const trend = this.calculateSimpleTrend(dataset.data);
          const trendIcon = trend > 0 ? " ↗" : trend < 0 ? " ↘" : " →";
          label.text += trendIcon;
        }

        return label;
      });
    },

    calculateSimpleTrend(data) {
      const validData = data.filter(
        (value) => value !== null && value !== undefined,
      );
      if (validData.length < 2) return 0;

      const first = validData[0];
      const last = validData[validData.length - 1];

      return ((last - first) / first) * 100;
    },

    getDatasetUnit(label) {
      const unitMap = {
        "10m Sprint": "s",
        "30m Sprint": "s",
        "Countermovement Jump": "cm",
        "Reactive Strength Index": "",
        "Fitness Test": "",
        Height: "cm",
        Weight: "kg",
        "Team Average": "s",
      };
      return unitMap[label] || "";
    },

    getCurrentUnit() {
      // This should be passed from parent or determined from context
      return "";
    },

    getYAxisLabel() {
      // This should be passed from parent or determined from context
      return "Performance Value";
    },

    getTooltipInsights(context) {
      const insights = [];

      if (context.length > 0) {
        const dataIndex = context[0].dataIndex;

        // Add comparison with previous value
        if (dataIndex > 0) {
          const current = context[0].parsed.y;
          const previous =
            context[0].chart.data.datasets[context[0].datasetIndex].data[
              dataIndex - 1
            ];

          if (previous !== null && previous !== undefined) {
            const change = current - previous;
            const percentage = ((change / previous) * 100).toFixed(1);

            if (Math.abs(change) > 0.01) {
              const direction = change > 0 ? "increase" : "decrease";
              insights.push(
                `${Math.abs(percentage)}% ${direction} from previous`,
              );
            }
          }
        }

        // Add percentile information if available
        // This would require team/age group data

        // Add session recommendations
        insights.push("Click for detailed analysis");
      }

      return insights;
    },
  },
};
</script>

<style scoped>
canvas {
  max-height: 500px;
}
</style>
